import { useMemo } from 'react';
import { useAppAuth } from '@aimsos/auth';
import { useToast } from '@aimsos/ui';
import {
  BasicChatPanel,
  ChatProviderWrapper,
  useChatHostRuntime,
} from '@aimsos/chat';
import { MessageSquare } from 'lucide-react';
import { api } from '../../../services';

/**
 * Right-hand column on agent edit (or list drawer): nested chat stack locked to the agent.
 * Reuses the shell WebSocket + API when {@link useChatHostRuntime} is available (Core Shell).
 *
 * @param {boolean} [playgroundBranding=true] — When false (e.g. list quick-chat), hero uses the agent name/description instead of "Test your Agent" copy.
 */
export default function AgentPlaygroundChatColumn({ t, agent, chatStarters, playgroundBranding = true }) {
  const hostRuntime = useChatHostRuntime();
  const { userId } = useAppAuth();
  const toast = useToast();

  const tenantId = useMemo(
    () => (typeof localStorage !== 'undefined' ? localStorage.getItem('workspace_uuid') || '' : ''),
    [],
  );

  const notifications = useMemo(
    () => ({
      success: (opts) => toast.success?.(opts),
      error: (opts) => toast.error?.(opts),
    }),
    [toast],
  );

  const authStore = useMemo(
    () => ({
      userId,
      currentWorkspaceUuid: tenantId,
      userData: {},
      currentUserFirstName: undefined,
    }),
    [userId, tenantId],
  );

  const lockAgent = useMemo(
    () =>
      agent
        ? {
            ...agent,
            uuid: agent.id,
            id: agent.id,
            name: agent.name,
            description: agent.description,
            model: agent.model,
            metadata: { description: agent.description, model: agent.model },
          }
        : null,
    [agent],
  );

  const previewDescription = useMemo(
    () => [t('edit.preview.subtitle'), t('edit.preview.hint')].filter(Boolean).join(' '),
    [t],
  );

  if (!agent?.id) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 bg-surface px-8 text-center text-sm text-content-muted">
        <MessageSquare className="h-8 w-8 text-content-faint" strokeWidth={1.5} aria-hidden />
        <p>{t('edit.preview.needSavedAgent')}</p>
      </div>
    );
  }

  const apiClient = hostRuntime?.apiClient ?? api;
  const wsService = hostRuntime?.wsService ?? null;
  const mergedAuthStore = hostRuntime?.authStore ?? authStore;
  const auth0User = hostRuntime?.auth0User ?? null;
  const mergedNotifications = hostRuntime?.notifications ?? notifications;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-surface">
      <ChatProviderWrapper
        apiClient={apiClient}
        wsService={wsService}
        userId={userId}
        tenantId={tenantId}
        notifications={mergedNotifications}
        authStore={mergedAuthStore}
        auth0User={auth0User}
      >
        <BasicChatPanel
          onClose={() => {}}
          layout="split"
          splitResizeSurface="host"
          translationNamespace="chat"
          embedded
          lockAgentId={agent.id}
          lockAgent={lockAgent}
          conversationStartersOverride={chatStarters}
          embeddedHeroTitle={playgroundBranding ? t('edit.preview.title') : null}
          embeddedHeroDescription={playgroundBranding ? previewDescription : null}
          composerPlaceholder={t('edit.preview.inputPlaceholder')}
        />
      </ChatProviderWrapper>
    </div>
  );
}
